import { Capacitor } from "@capacitor/core";
import { throwErrorWithStack } from "./crashlytics";
import store from "./store";
import { Device, Share, FirebaseDynamicLinks } from "./plugins";
import { logEvent } from "./analytics";
// import { translate } from "./internationalization";
import { noLivesElCtaInviteFriend } from "./elements";
import { showErrorSnackbar, showSuccessSnackbar } from "./snackbar";
import config, {
  IS_WEB,
  NUM_LIVES_INVITE_FRIEND_INVITEE,
  NUM_LIVES_INVITE_FRIEND_INVITER,
} from "./config";
import { updateLives } from "./lives";
import cachedFirestore from "./cachedFirestore";
import { getLogger } from "./logging";

const log = getLogger("Share");

// export async function shareCb(e) {
//   e.stopImmediatePropagation();
//   const shareObject = {
//     title: translate("Trap the black cat"),
//     text: `${translate("I've reached level _").replace(
//       "_",
//       store.maxLevelIdx + 1
//     )}! ${translate("Can you beat me?")}`,
//     url: "https://play.google.com/store/apps/details?id=com.kima.chatnoirhex",
//     dialogTitle: translate("Share your progress"),
//   };
//   log.debug("Sharing", shareObject);
//   try {
//     const result = await Share.share(shareObject);
//     log.info(result);
//     logEvent("share");
//   } catch (error) {
//     throwErrorWithStack(error);
//     logEvent("share_error");
//   }
// }

const addInvitee = async (inviter) => {
  const { identifier } = await Device.getId();
  try {
    const data = await cachedFirestore.getDocument({
      reference: "invites/inviter",
    });
    const existingData = (data && data[inviter]) || [];
    if (existingData.includes(identifier)) return;
    await cachedFirestore.updateDocument({
      reference: "invites/inviter",
      data: {
        [inviter]: [identifier, ...existingData],
      },
    });
  } catch (error) {
    throwErrorWithStack(error);
  }
};

export const hasInvitees = async () => {
  if (IS_WEB) return;
  try {
    const { identifier } = await Device.getId();
    const inviterId = identifier;
    const data = await cachedFirestore.getDocument({
      reference: `invites/inviter`,
    });

    if (data && data.invitees) {
      const inviteeIds = data[inviterId];
      if (inviteeIds && inviteeIds.length > 0) {
        const newInviteeIds = inviteeIds.filter(
          (inviteeId) =>
            !store.inviteeIds.includes(inviteeId) && inviteeId != inviterId
        );
        if (newInviteeIds.length > 0) {
          const newLives =
            newInviteeIds.length * NUM_LIVES_INVITE_FRIEND_INVITER;
          store.set("numLives", store.numLives + newLives);
          store.set("inviteeIds", inviteeIds);
          showSuccessSnackbar(
            newInviteeIds.length == 1
              ? `You got ${newLives} lives from your friend! 🎉`
              : `You got ${newLives} lives from ${newInviteeIds.length} friends! 🎉`
          );
          updateLives();
        }
      }
      return true;
    } else {
      return false;
    }
  } catch (error) {
    throwErrorWithStack(error);
  }
};

export async function createInviteFriendShortLink() {
  if (store.inviteFriendDeepLink || IS_WEB) return;
  const { identifier } = await Device.getId();
  const inviterId = identifier;
  const dynShortLinkConfig = {
    domainUriPrefix: "https://chatnoirhex.page.link",
    uri: `https://chatnoirhex.page.link/sharing?id=${inviterId}`,
    androidParameters: {
      packageName: config.ID,
      versionCode: config.BUILD,
    },
    iosParameters: { bundleId: config.ID },
  };
  if (!__DEV__)
    dynShortLinkConfig.googleAnalytics = {
      source: "referral",
      medium: "friend",
      campaign: "invite_friends_to_earn_lives",
      term: "",
      content: "",
    };
  try {
    const link = await FirebaseDynamicLinks.createDynamicShortLink(
      dynShortLinkConfig
    ); // e.g. https://chatnoirhex.page.link/sharing?id=${identifier}
    store.set("inviteFriendDeepLink", link && link.value);
  } catch (error) {
    throwErrorWithStack(error);
  }
}

const validateDeepLink = async (deepLink) => {
  const url = new URL(deepLink);
  const path = url.pathname; // e.g. /sharing
  if (path != "/sharing") return;
  const uid = url.searchParams.get("id");
  if (!uid) return;
  return uid;
};

export function listenToDeepLinkOpen() {
  if (store.receivedInviteeBonus) return;
  FirebaseDynamicLinks.addListener("deepLinkOpen", async (data) => {
    if (!data.url) return;
    const inviterId = await validateDeepLink(data.url);
    if (!inviterId) return;
    const { identifier } = await Device.getId();
    if (inviterId == identifier) {
      showErrorSnackbar("You can't invite yourself");
      return;
    }
    store.set("numLives", store.numLives + NUM_LIVES_INVITE_FRIEND_INVITEE);
    showSuccessSnackbar(
      `You got ${NUM_LIVES_INVITE_FRIEND_INVITEE} lives from your friend! 🎉`
    );
    updateLives();
    store.set("receivedInviteeBonus", true);
    addInvitee(inviterId);
  });
}

export async function inviteFriendCb(e) {
  e.stopImmediatePropagation();
  if (!store.inviteFriendDeepLink) {
    await createInviteFriendShortLink();
    if (!store.inviteFriendDeepLink) {
      let errorMsg = "Error creating invite link 😢";
      if (!store.internetConnected)
        errorMsg += " Please check your internet connection 📡";
      showErrorSnackbar(errorMsg);
      return;
    }
  }
  const shareObject = {
    title: "Join Me on This Adventure!",
    text: "Hey there! I've been enjoying this game and thought you'd love to play too. Give it a go:",
    url: store.inviteFriendDeepLink,
    dialogTitle: "Invite a Friend to Play!",
  };
  log.debug("Sharing", shareObject);
  try {
    const result = await Share.share(shareObject);
    log.debug(result);
    logEvent("invite_friend");
  } catch (error) {
    logEvent("share_error");
    if (error.message != "Share canceled") throwErrorWithStack(error);
  }
}

export async function removeShareIfNotSupported() {
  try {
    const { value } = await Share.canShare();
    if (!value && Capacitor.getPlatform() != "web") {
      log.warn("Share not supported");
      noLivesElCtaInviteFriend.style.display = "none";
    }
  } catch (error) {
    throwErrorWithStack(error);
  }
}
